'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  Wrench, Stethoscope, Car, Sparkles, MessageSquare, Search,
  Globe, Moon, Sun, LayoutGrid, Gavel, ArrowUpRight, BadgeCheck,
  Boxes, CircleDollarSign, Gauge, ShieldCheck, Store, Plus,
  ChevronDown, SlidersHorizontal, X, Star, MapPin, Calculator,
  TrendingUp, Filter, ArrowRight, Zap, Eye, Users, Clock,
  ChevronRight, User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from '@/context/LanguageContext';
import { useCountry, COUNTRIES } from '@/context/CountryContext';
import { useTheme } from '@/context/ThemeContext';
import { AIPersonalizedRecommendations } from '@/components/AIPersonalizedRecommendations';
import { useBranding } from '@/context/BrandingContext';
import { adsApi } from '@/lib/api/ads';
import type { AdItem } from '@/lib/api/ads';
import { workshopsApi } from '@/lib/api/workshops';
import { MOCK_LISTINGS } from '@/app/lib/mock-db';

/* ── Ripple helper ─────────────────────────────────────── */
function useRipple() {
  const addRipple = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    const span = document.createElement('span');
    span.className = 'ripple-circle';
    span.style.cssText = `width:${size}px;height:${size}px;top:${y}px;left:${x}px`;
    el.appendChild(span);
    span.addEventListener('animationend', () => span.remove());
  }, []);
  return addRipple;
}

/* ── Intersection observer for fade-in ─────────────────── */
function useFadeIn() {
  useEffect(() => {
    const els = document.querySelectorAll('.section-fade');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── Car makes for filter ──────────────────────────────── */
const CAR_MAKES = ['Toyota', 'BMW', 'Mercedes', 'Ford', 'Hyundai', 'Honda', 'Kia', 'Nissan', 'Audi', 'Chevrolet', 'Lexus', 'Land Rover'];
const BODY_TYPES = ['Sedan', 'SUV', 'Pickup', 'Van', 'Coupe', 'Hatchback', 'Convertible'];
const FUEL_TYPES = ['Petrol', 'Diesel', 'Electric', 'Hybrid'];
const TRANSMISSIONS = ['Automatic', 'Manual'];
const CONDITIONS = ['New', 'Used', 'Certified Pre-Owned'];
const GROUP_BY_OPTIONS = ['Category', 'Brand', 'Price Range', 'Year', 'Location'];
const YEARS = Array.from({ length: 25 }, (_, i) => 2025 - i);

/* ── Auction calculator logic ─────────────────────────── */
function calcAuctionPrice(make: string, year: number, km: number, condition: string): number {
  const basePrices: Record<string, number> = {
    Toyota: 85000, BMW: 150000, Mercedes: 160000, Ford: 90000,
    Hyundai: 70000, Honda: 80000, Kia: 65000, Nissan: 75000,
    Audi: 145000, Chevrolet: 85000, Lexus: 130000, 'Land Rover': 200000,
  };
  const base = basePrices[make] || 80000;
  const age = 2025 - year;
  const ageDep = Math.max(0.3, 1 - age * 0.07);
  const kmDep = Math.max(0.5, 1 - (km / 1000) * 0.002);
  const condMult = condition === 'New' ? 1 : condition === 'Certified Pre-Owned' ? 0.9 : 0.78;
  return Math.round(base * ageDep * kmDep * condMult / 500) * 500;
}

const LOCALES = {
  ar: {
    badge: "سوق السيارات المتقدم",
    heroTitlePart1: "الشراء، البيع، قطع الغيار والخدمات في",
    heroTitleAccent: "مسار موحد",
    heroTitlePart2: "واحترافي.",
    heroSub: "الشراء والبيع وقطع الغيار والخدمات في مسار موحد واحترافي. الطريق الأسهل للعثور على السيارة المناسبة أو الوصول لقطع الغيار والورش.",
    searchPlaceholder: "ابحث عن سيارات، قطع غيار، ورش صيانة...",
    filterBtn: "تصفية",
    searchBtn: "بحث",
    startBuying: "ابدأ الشراء",
    sellMyCar: "بع سيارتي",
    mobilePostBtn: "أعلن",
    
    // Scopes
    all: "الكل",
    cars: "السيارات",
    parts: "قطع الغيار",
    services: "الخدمات",
    workshops: "الورش",
    marketplace: "المتجر",

    // Advanced Filters
    advFilters: "خيارات تصفية متقدمة",
    clearAll: "مسح الكل",
    make: "الماركة",
    allMakes: "كل الماركات",
    bodyType: "فئة السيارة",
    allTypes: "كل الفئات",
    fuelType: "نوع الوقود",
    allFuels: "كل أنواع الوقود",
    transmission: "ناقل الحركة",
    allTrans: "الكل",
    condition: "الحالة",
    anyCond: "أي حالة",
    groupBy: "تجميع حسب",
    none: "بدون تجميع",
    yearLabel: "سنة الصنع",
    maxPrice: "الحد الأقصى للسعر",
    applyFilters: "تطبيق التصفية والبحث",

    // Live Card
    liveAuction: "مزاد مباشر",
    currentBid: "العرض الحالي",
    aiReport: "تقرير الذكاء الاصطناعي",
    verified: "موثق 100%",
    activity: "النشاط الحالي",
    bidders: "24 مزايد",
    inventory: "المخزون",
    partsCount: "54 ألف قطعة",
    workshopRating: "تقييم 4.9/5",
    systemOnline: "النظام متصل",

    // Services
    mainServicesTitle: "اكتشف خدماتنا الرئيسية",
    mainServicesSub: "اختر مسارك",
    blueHint: "الأزرق للبحث والشراء",
    orangeHint: "البرتقالي للبيع والخدمات",
    open: "فتح",

    // Main services translations
    buyLabel: "معاملات المركبات",
    buySub: "الشراء والمزادات",
    buyDesc: "سيارات مفحوصة، تفاصيل مزادات واضحة، وخدمات مزايدة فورية.",

    sellLabel: "بيع المركبات",
    sellSub: "أعلن عن سيارتك",
    sellDesc: "تحديد أسرع للأسعار وقوائم إعلانات منسقة.",

    marketplaceLabel: "السوق المفتوح",
    marketplaceSub: "السلع العامة",
    marketplaceDesc: "إعلانات عامة لمختلف السلع ومستلزمات السيارات.",

    partsLabel: "قطع غيار أصلية",
    partsSub: "مستلزمات وقطع",
    partsDesc: "ابحث عن قطع الغيار حسب نوع سيارتك والموردين القريبين.",

    maintenanceLabel: "ورش صيانة",
    maintenanceSub: "خدمات الإصلاح",
    maintenanceDesc: "اعثر على أفضل ورش الصيانة ومراكز الخدمة القريبة منك.",

    rentalsLabel: "تأجير المركبات",
    rentalsSub: "إيجار السيارات",
    rentalsDesc: "خيارات قيادة شهرية وتأجير مرن بالوقت.",

    servicesLabel: "الخدمات المتكاملة",
    servicesSub: "مركز الخدمات",
    servicesDesc: "الفحص الذكي للسيارات، والخدمات المركزية الشاملة.",

    auctionsLabel: "مزادات خاصة",
    auctionsSub: "مركبات مميزة",
    auctionsDesc: "وصول مباشر وحصري لأقوى المزادات الفاخرة.",

    // AI Services Strip
    diagnoseLabel: "التشخيص الذكي بالذكاء الاصطناعي",
    diagnoseSub: "أداة متطورة للتشخيص الصوتي وتحديد الأعطال بدقة.",
    inspectionLabel: "الرؤية الذكية بالذكاء الاصطناعي",
    inspectionSub: "فحص بصري ذكي للسيارات بالذكاء الاصطناعي عبر الكاميرا.",

    // Recommended Cars
    recTitle: "السيارات الموصى بها",
    recSub: "مختارة بعناية لك",
    viewAll: "عرض الكل",
    available: "متاح",
    featured: "مميز",

    // Calculator
    calcTitle: "الحاسبة الذكية لمزادات السيارات",
    calcSub: "التسعير الذكي",
    calcMake: "ماركة السيارة",
    calcYear: "سنة الصنع",
    calcKm: "المسافة المقطوعة (بالألف كم)",
    calcCondition: "الحالة",
    calcBtn: "احسب قيمة السيارة المتوقعة",
    calcEst: "القيمة المتوقعة في المزاد",
    calcBasedOn: "بناءً على بيانات السوق لسيارات",
    calcBrowse: "تصفح المزادات الحالية ←",
    calcPlaceholderTitle: "أدخل بيانات سيارتك واضغط على زر الحساب",
    calcPlaceholderSub: "احصل على تسعير فوري متوقع لقيمة سيارتك",

    // Workshops
    wsTitle: "أفضل ورش الصيانة",
    wsSub: "مراكز صيانة معتمدة",
    wsAvailable: "متاح الآن",

    // Footer CTA
    ctaTitle: "جاهز لبيع سيارتك؟",
    ctaSub: "أضف إعلانك في دقائق معدودة. وتواصل مع آلاف المشترين الجادين.",
    ctaBtn: "أضف إعلان سيارتك مجاناً"
  },
  en: {
    badge: "Advanced Automotive Marketplace",
    heroTitlePart1: "Buy, sell, parts, and services in one",
    heroTitleAccent: "polished",
    heroTitlePart2: "flow.",
    heroSub: "Buy, sell, parts, and services in one polished flow. The clearest way to find the right car or reach spare parts and workshops.",
    searchPlaceholder: "Search cars, parts, workshops...",
    filterBtn: "Filter",
    searchBtn: "Search",
    startBuying: "Start Buying",
    sellMyCar: "Sell My Car",
    mobilePostBtn: "Post",

    // Scopes
    all: "All",
    cars: "Cars",
    parts: "Parts",
    services: "Services",
    workshops: "Workshops",
    marketplace: "Market",

    // Advanced Filters
    advFilters: "Advanced Filters",
    clearAll: "Clear All",
    make: "Make",
    allMakes: "All Makes",
    bodyType: "Body Type",
    allTypes: "All Types",
    fuelType: "Fuel Type",
    allFuels: "All Fuels",
    transmission: "Transmission",
    allTrans: "All",
    condition: "Condition",
    anyCond: "Any",
    groupBy: "Group By",
    none: "None",
    yearLabel: "Year",
    maxPrice: "Max Price",
    applyFilters: "Apply Filters & Search",

    // Live Card
    liveAuction: "Live Auction",
    currentBid: "Current Bid",
    aiReport: "AI Report",
    verified: "100% Verified",
    activity: "Activity",
    bidders: "24 Bidders",
    inventory: "Inventory",
    partsCount: "54k Parts",
    workshopRating: "4.9/5 Rated",
    systemOnline: "System Online",

    // Services
    mainServicesTitle: "Discover Our Main Services",
    mainServicesSub: "Choose your path",
    blueHint: "Blue for search and buying",
    orangeHint: "Orange for selling and service",
    open: "Open",

    // Main services translations
    buyLabel: "Vehicle Transactions",
    buySub: "Purchase & Auctions",
    buyDesc: "Verified cars, clear auction details, and instant auction services.",

    sellLabel: "Vehicle Selling",
    sellSub: "Post Your Car",
    sellDesc: "Faster pricing and cleaner listings.",

    marketplaceLabel: "Marketplace",
    marketplaceSub: "Public Goods",
    marketplaceDesc: "Open listings for auto goods.",

    partsLabel: "Genuine",
    partsSub: "Spare Parts",
    partsDesc: "Parts by vehicle and supplier nearby.",

    maintenanceLabel: "Workshop",
    maintenanceSub: "Services",
    maintenanceDesc: "Workshops and service nearby.",

    rentalsLabel: "Vehicle",
    rentalsSub: "Rentals",
    rentalsDesc: "Vehicle monthly driving options and clock options.",

    servicesLabel: "Integrated Services",
    servicesSub: "Hub",
    servicesDesc: "One/multi rentals, Smart Inspection, and central vehicle hub.",

    auctionsLabel: "Specialized Vehicle",
    auctionsSub: "Auctions",
    auctionsDesc: "Mazda-style refined access.",

    // AI Services Strip
    diagnoseLabel: "AI Diagnose",
    diagnoseSub: "A highly detailed, polished mic and stethoscope tool.",
    inspectionLabel: "AI Vision",
    inspectionSub: "AI vision, detailed camera lens and camaloi glowing stars.",

    // Recommended Cars
    recTitle: "Recommended Cars",
    recSub: "Handpicked For You",
    viewAll: "View All",
    available: "Available",
    featured: "Featured",

    // Calculator
    calcTitle: "New Auction Price Calculator",
    calcSub: "Smart Pricing",
    calcMake: "Car Make",
    calcYear: "Year",
    calcKm: "Mileage (×1000 km)",
    calcCondition: "Condition",
    calcBtn: "Calculate Auction Price",
    calcEst: "Estimated Auction Value",
    calcBasedOn: "Based on market data for",
    calcBrowse: "Browse Auctions →",
    calcPlaceholderTitle: "Fill in the details and click Calculate",
    calcPlaceholderSub: "Get an estimated auction price instantly",

    // Workshops
    wsTitle: "Top Workshops",
    wsSub: "Trusted Professionals",
    wsAvailable: "Available Now",

    // Footer CTA
    ctaTitle: "Ready to sell your car?",
    ctaSub: "Post your listing in minutes. Reach thousands of verified buyers.",
    ctaBtn: "Post My Car for Free"
  }
};

const FUEL_TYPES_TRANS: Record<string, Record<'ar' | 'en', string>> = {
  Petrol: { ar: 'بنزين', en: 'Petrol' },
  Diesel: { ar: 'ديزل', en: 'Diesel' },
  Electric: { ar: 'كهرباء', en: 'Electric' },
  Hybrid: { ar: 'هجين', en: 'Hybrid' },
};

const TRANSMISSIONS_TRANS: Record<string, Record<'ar' | 'en', string>> = {
  Automatic: { ar: 'أوتوماتيك', en: 'Automatic' },
  Manual: { ar: 'يدوي', en: 'Manual' },
};

const CONDITIONS_TRANS: Record<string, Record<'ar' | 'en', string>> = {
  New: { ar: 'جديد', en: 'New' },
  Used: { ar: 'مستعمل', en: 'Used' },
  'Certified Pre-Owned': { ar: 'مضمون معتمد', en: 'Certified Pre-Owned' },
};

const GROUP_BY_TRANS: Record<string, Record<'ar' | 'en', string>> = {
  Category: { ar: 'الفئة', en: 'Category' },
  Brand: { ar: 'الماركة', en: 'Brand' },
  'Price Range': { ar: 'نطاق السعر', en: 'Price Range' },
  Year: { ar: 'السنة', en: 'Year' },
  Location: { ar: 'الموقع', en: 'Location' },
};

const BODY_TYPES_TRANS: Record<string, Record<'ar' | 'en', string>> = {
  Sedan: { ar: 'سيدان', en: 'Sedan' },
  SUV: { ar: 'دفع رباعي', en: 'SUV' },
  Pickup: { ar: 'بيك أب', en: 'Pickup' },
  Van: { ar: 'فان', en: 'Van' },
  Coupe: { ar: 'كوبيه', en: 'Coupe' },
  Hatchback: { ar: 'هاتشباك', en: 'Hatchback' },
  Convertible: { ar: 'مكشوفة', en: 'Convertible' },
};

export default function Home() {
  const router = useRouter();
  const { t, isRTL, lang, toggleLanguage } = useLanguage();
  const { setSelectedCountryCode, selectedCountry, selectedCountryCode } = useCountry();
  const { isDark, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const addRipple = useRipple();
  useFadeIn();

  /* Search state */
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState<'all' | 'cars' | 'parts' | 'services' | 'workshops' | 'marketplace'>('all');
  const [showFilters, setShowFilters] = useState(false);

  /* Filter state */
  const [filterMake, setFilterMake] = useState('');
  const [filterYearFrom, setFilterYearFrom] = useState(2010);
  const [filterYearTo, setFilterYearTo] = useState(2025);
  const [filterPriceMax, setFilterPriceMax] = useState(500000);
  const [filterBodyType, setFilterBodyType] = useState('');
  const [filterFuel, setFilterFuel] = useState('');
  const [filterTransmission, setFilterTransmission] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  const [filterGroupBy, setFilterGroupBy] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  /* Auction calculator */
  const [calcMake, setCalcMake] = useState('Toyota');
  const [calcYear, setCalcYear] = useState(2020);
  const [calcKm, setCalcKm] = useState(50);
  const [calcCondition, setCalcCondition] = useState('Used');
  const [calcResult, setCalcResult] = useState<number | null>(null);
  const currencyLabel =
    lang === 'ar'
      ? selectedCountry?.currency_symbol || selectedCountry?.currency || 'ر.ع'
      : selectedCountry?.currency_code || (selectedCountryCode === 'OM' ? 'OMR' : 'SAR');

  /* Ads / workshops */
  const [homeAds, setHomeAds] = useState<AdItem[]>([]);
  const [workshops, setWorkshops] = useState<any[]>([]);
  const [rentalsEnabled, setRentalsEnabled] = useState(true);

  useEffect(() => {
    adsApi.list({ placement: 'homepage' }).then((r) => setHomeAds(r.slice(0, 6))).catch(() => {});
    workshopsApi.getWorkshops({}).then((r: any) => {
      const data = Array.isArray(r) ? r : r.results || [];
      setWorkshops(data.slice(0, 6));
    }).catch(() => {});
    const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    fetch(`${API}/api/settings/public/`).then(r => r.json()).then(d => {
      setRentalsEnabled(!['0', 'false', 'no', 'off'].includes(String(d.rentals_enabled || '').toLowerCase()));
    }).catch(() => {});
  }, []);

  const submitSearch = () => {
    const q = searchQuery.trim();
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (filterMake) params.set('make', filterMake);
    if (filterBodyType) params.set('body', filterBodyType);
    if (filterFuel) params.set('fuel', filterFuel);
    if (filterCondition) params.set('condition', filterCondition);
    if (filterGroupBy) params.set('group', filterGroupBy);
    params.set('year_from', String(filterYearFrom));
    params.set('year_to', String(filterYearTo));
    params.set('price_max', String(filterPriceMax));
    const normalizeCode = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '-');
    const carParams = new URLSearchParams();
    if (q) carParams.set('search', q);
    if (filterMake) carParams.set('make', normalizeCode(filterMake));
    if (filterBodyType) carParams.set('type', normalizeCode(filterBodyType));
    if (filterFuel) carParams.set('fuel_type', normalizeCode(filterFuel));
    if (filterTransmission) carParams.set('transmission', normalizeCode(filterTransmission));
    carParams.set('yearFrom', String(filterYearFrom));
    carParams.set('yearTo', String(filterYearTo));
    carParams.set('priceMax', String(filterPriceMax));
    const destinations: Record<string, string> = {
      all: `/search?${params}`,
      cars: `/buy/results?${carParams}`,
      parts: `/parts?search=${encodeURIComponent(q)}`,
      services: `/services?search=${encodeURIComponent(q)}`,
      workshops: `/maintenance?search=${encodeURIComponent(q)}`,
      marketplace: `/marketplace?search=${encodeURIComponent(q)}`,
    };
    router.push(destinations[searchScope]);
  };

  const mainServices = [
    { id: 'buy', label: 'Vehicle Transactions', sub: 'Purchase & Auctions', desc: 'Verified cars, clear auction details, and instant auction services.', icon: Car, color: 'bg-blue-600', iconBg: 'bg-blue-50 text-blue-700', accent: 'from-blue-600 to-sky-400', href: '/buy' },
    { id: 'sell', label: 'Vehicle Selling', sub: 'Post Your Car', desc: 'Faster pricing and cleaner listings.', icon: CircleDollarSign, color: 'bg-orange-500', iconBg: 'bg-orange-50 text-orange-700', accent: 'from-orange-500 to-amber-300', href: '/sell', highlight: true },
    { id: 'marketplace', label: 'Marketplace', sub: 'Public Goods', desc: 'Open listings for auto goods.', icon: Store, color: 'bg-emerald-600', iconBg: 'bg-emerald-50 text-emerald-700', accent: 'from-emerald-500 to-teal-300', href: '/marketplace' },
    { id: 'parts', label: 'Genuine', sub: 'Spare Parts', desc: 'Parts by vehicle and supplier nearby.', icon: Boxes, color: 'bg-indigo-600', iconBg: 'bg-indigo-50 text-indigo-700', accent: 'from-indigo-600 to-blue-400', href: '/parts' },
    { id: 'maintenance', label: 'Workshop', sub: 'Services', desc: 'Workshops and service nearby.', icon: Wrench, color: 'bg-amber-600', iconBg: 'bg-amber-50 text-amber-700', accent: 'from-amber-500 to-orange-300', href: '/maintenance' },
    ...(rentalsEnabled ? [{ id: 'rentals', label: 'Vehicle', sub: 'Rentals', desc: 'Vehicle monthly driving options and clock options.', icon: Gauge, color: 'bg-violet-600', iconBg: 'bg-violet-50 text-violet-700', accent: 'from-violet-500 to-fuchsia-400', href: '/rentals' }] : []),
    { id: 'services', label: 'Integrated Services', sub: 'Hub', desc: 'One/multi rentals, Smart Inspection, and central vehicle hub.', icon: LayoutGrid, color: 'bg-sky-600', iconBg: 'bg-sky-50 text-sky-700', accent: 'from-sky-500 to-cyan-300', href: '/services' },
    { id: 'auctions', label: 'Specialized Vehicle', sub: 'Auctions', desc: 'Mazda-style refined access.', icon: Gavel, color: 'bg-rose-600', iconBg: 'bg-rose-50 text-rose-700', accent: 'from-rose-500 to-pink-400', href: '/auctions' },
  ];

  const translatedMainServices = mainServices.map((svc) => ({
    ...svc,
    label: LOCALES[lang as 'ar' | 'en']?.[`${svc.id}Label` as keyof typeof LOCALES['ar']] || svc.label,
    sub: LOCALES[lang as 'ar' | 'en']?.[`${svc.id}Sub` as keyof typeof LOCALES['ar']] || svc.sub,
    desc: LOCALES[lang as 'ar' | 'en']?.[`${svc.id}Desc` as keyof typeof LOCALES['ar']] || svc.desc,
  }));

  const aiServices = [
    {
      id: 'diagnose', label: 'AI Diagnose', sub: 'A highly detailed, polished mic and stethoscope tool.',
      icon: Stethoscope, color: 'text-emerald-400', bg: 'from-slate-800 to-slate-900',
      accent: 'border-emerald-500/30', href: '/diagnose',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&q=80',
    },
    {
      id: 'inspection', label: 'AI Vision', sub: 'AI vision, detailed camera lens and camaloi glowing stars.',
      icon: Eye, color: 'text-blue-400', bg: 'from-blue-900 to-indigo-900',
      accent: 'border-blue-500/30', href: '/inspection',
      image: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=400&q=80',
    },
  ];

  const translatedAiServices = aiServices.map((svc) => ({
    ...svc,
    label: LOCALES[lang as 'ar' | 'en']?.[`${svc.id}Label` as keyof typeof LOCALES['ar']] || svc.label,
    sub: LOCALES[lang as 'ar' | 'en']?.[`${svc.id}Sub` as keyof typeof LOCALES['ar']] || svc.sub,
  }));

  const searchScopes = [
    { id: 'all', label: 'All' }, { id: 'cars', label: 'Cars' },
    { id: 'parts', label: 'Parts' }, { id: 'services', label: 'Services' },
    { id: 'workshops', label: 'Workshops' }, { id: 'marketplace', label: 'Market' },
  ] as const;

  const translatedSearchScopes = searchScopes.map(scope => ({
    ...scope,
    label: LOCALES[lang as 'ar' | 'en']?.[scope.id as keyof typeof LOCALES['ar']] || scope.label
  }));

  const recommendedCars = MOCK_LISTINGS.slice(0, 6);

  const activeFiltersCount = [filterMake, filterBodyType, filterFuel, filterTransmission, filterCondition, filterGroupBy, filterLocation].filter(Boolean).length;

  return (
    <div className="flex flex-col w-full min-h-screen bg-[#F0F4FF] dark:bg-slate-950">

      {/* ══════════════════════════════════════════════════════
          MOBILE HEADER
      ══════════════════════════════════════════════════════ */}
      <header className="md:hidden p-3 flex items-center justify-between bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 sticky top-0 z-50 shadow-sm">
        <Link href="/" className="flex items-center gap-2">
          <img src="/maknna_logo.png" alt="Maknna" className="h-8 w-8 rounded-lg object-cover" />
          <span className="text-lg font-black text-slate-900 dark:text-white">Makn<span className="text-orange-500">na</span></span>
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={toggleLanguage} className="h-8 px-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-black text-slate-600 dark:text-slate-300">
            {lang === 'ar' ? 'EN' : 'AR'}
          </button>
          <button onClick={toggleTheme} className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500">
            {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link href="/sell">
            <button className="h-8 px-3 rounded-lg bg-orange-500 text-white text-xs font-black flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> {LOCALES[lang as 'ar' | 'en']?.mobilePostBtn || 'Post'}
            </button>
          </Link>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════════════════════ */}
      <section className="mx-4 md:mx-8 my-6 md:my-8 relative isolate overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0f2044] to-[#1a3a6e] px-6 py-16 md:py-28 text-white rounded-[2rem] md:rounded-[3.5rem] border border-white/10 shadow-2xl">
        {/* Background patterns */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-orange-500/10 blur-[80px]" />

        <div className="relative max-w-[1440px] mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.15em] text-blue-200 backdrop-blur">
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                {LOCALES[lang as 'ar' | 'en']?.badge}
              </div>

              <h1 className="text-3xl md:text-5xl font-black leading-[1.3] md:leading-[1.1] text-white">
                {LOCALES[lang as 'ar' | 'en']?.heroTitlePart1}{' '}
                <span className="italic text-gradient-orange bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent">
                  {LOCALES[lang as 'ar' | 'en']?.heroTitleAccent}
                </span>{' '}{LOCALES[lang as 'ar' | 'en']?.heroTitlePart2}
              </h1>
              <p className="text-slate-300 text-sm font-medium leading-relaxed max-w-lg">
                {LOCALES[lang as 'ar' | 'en']?.heroSub}
              </p>

              {/* ── Advanced Search Bar ── */}
              <div className="space-y-3">
                <form
                  onSubmit={(e) => { e.preventDefault(); submitSearch(); }}
                  className="relative"
                >
                  <div className="flex items-center gap-2 h-14 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-2xl shadow-[0_20px_50px_-15px_rgba(29,78,216,0.3)] overflow-hidden border border-white/20 pr-2 focus-within:ring-2 focus-within:ring-orange-500/20 transition-all duration-200">
                    <Search className="shrink-0 h-5 w-5 text-slate-400 mx-4" />
                    <input
                      type="search"
                      placeholder={LOCALES[lang as 'ar' | 'en']?.searchPlaceholder}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 h-full bg-transparent text-sm font-medium text-slate-800 placeholder:text-slate-400 outline-none"
                    />
                    {/* Filter toggle */}
                    <button
                      type="button"
                      onClick={() => setShowFilters(!showFilters)}
                      className={`shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-black transition-colors ${showFilters ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      {LOCALES[lang as 'ar' | 'en']?.filterBtn}
                      {activeFiltersCount > 0 && (
                        <span className="bg-orange-500 text-white rounded-full w-4 h-4 text-[9px] flex items-center justify-center font-black">{activeFiltersCount}</span>
                      )}
                    </button>
                    <button
                      type="submit"
                      className="shrink-0 h-10 px-5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-black transition-colors shadow-md shadow-orange-300/30"
                    >
                      {LOCALES[lang as 'ar' | 'en']?.searchBtn}
                    </button>
                  </div>
                </form>

                {/* Scope chips */}
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                  {translatedSearchScopes.map((scope) => (
                    <button
                      key={scope.id}
                      onClick={() => setSearchScope(scope.id)}
                      className={`shrink-0 rounded-full border px-4 py-1.5 text-[11px] font-black transition-all duration-150 ${
                        searchScope === scope.id
                          ? 'border-blue-400 bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                          : 'border-white/15 bg-white/8 text-blue-100 hover:border-orange-300 hover:text-orange-200'
                      }`}
                    >
                      {scope.label}
                    </button>
                  ))}
                </div>

                {/* ── Advanced Filter Panel ── */}
                {showFilters && (
                  <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-2xl shadow-blue-900/20 border border-white/20 space-y-5 text-slate-800">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-black text-slate-800 flex items-center gap-2">
                        <Filter className="h-4 w-4 text-blue-600" /> {LOCALES[lang as 'ar' | 'en']?.advFilters}
                      </h3>
                      <button onClick={() => {
                        setFilterMake(''); setFilterBodyType(''); setFilterFuel('');
                        setFilterTransmission(''); setFilterCondition(''); setFilterGroupBy('');
                        setFilterLocation(''); setFilterYearFrom(2010); setFilterYearTo(2025); setFilterPriceMax(500000);
                      }} className="text-xs text-red-500 font-bold hover:text-red-600">
                        <X className="h-3.5 w-3.5 inline mr-1" />{LOCALES[lang as 'ar' | 'en']?.clearAll}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {/* Make */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.make}</label>
                        <select value={filterMake} onChange={e => setFilterMake(e.target.value)}
                          className="w-full h-9 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 px-3 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                          <option value="">{LOCALES[lang as 'ar' | 'en']?.allMakes}</option>
                          {CAR_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>

                      {/* Body Type */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.bodyType}</label>
                        <select value={filterBodyType} onChange={e => setFilterBodyType(e.target.value)}
                          className="w-full h-9 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 px-3 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                          <option value="">{LOCALES[lang as 'ar' | 'en']?.allTypes}</option>
                          {BODY_TYPES.map(b => <option key={b} value={b}>{BODY_TYPES_TRANS[b]?.[lang as 'ar' | 'en'] || b}</option>)}
                        </select>
                      </div>

                      {/* Fuel */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.fuelType}</label>
                        <select value={filterFuel} onChange={e => setFilterFuel(e.target.value)}
                          className="w-full h-9 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 px-3 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                          <option value="">{LOCALES[lang as 'ar' | 'en']?.allFuels}</option>
                          {FUEL_TYPES.map(f => <option key={f} value={f}>{FUEL_TYPES_TRANS[f]?.[lang as 'ar' | 'en'] || f}</option>)}
                        </select>
                      </div>

                      {/* Transmission */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.transmission}</label>
                        <select value={filterTransmission} onChange={e => setFilterTransmission(e.target.value)}
                          className="w-full h-9 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 px-3 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                          <option value="">{LOCALES[lang as 'ar' | 'en']?.allTrans}</option>
                          {TRANSMISSIONS.map(t => <option key={t} value={t}>{TRANSMISSIONS_TRANS[t]?.[lang as 'ar' | 'en'] || t}</option>)}
                        </select>
                      </div>

                      {/* Condition */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.condition}</label>
                        <select value={filterCondition} onChange={e => setFilterCondition(e.target.value)}
                          className="w-full h-9 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 px-3 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                          <option value="">{LOCALES[lang as 'ar' | 'en']?.anyCond}</option>
                          {CONDITIONS.map(c => <option key={c} value={c}>{CONDITIONS_TRANS[c]?.[lang as 'ar' | 'en'] || c}</option>)}
                        </select>
                      </div>

                      {/* Group By */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.groupBy}</label>
                        <select value={filterGroupBy} onChange={e => setFilterGroupBy(e.target.value)}
                          className="w-full h-9 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 px-3 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                          <option value="">{LOCALES[lang as 'ar' | 'en']?.none}</option>
                          {GROUP_BY_OPTIONS.map(g => <option key={g} value={g}>{GROUP_BY_TRANS[g]?.[lang as 'ar' | 'en'] || g}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Year range */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        {LOCALES[lang as 'ar' | 'en']?.yearLabel}: {filterYearFrom} – {filterYearTo}
                      </label>
                      <div className="flex gap-3 items-center">
                        <input type="range" min={2000} max={2025} value={filterYearFrom}
                          onChange={e => setFilterYearFrom(Math.min(Number(e.target.value), filterYearTo))}
                          className="flex-1" />
                        <input type="range" min={2000} max={2025} value={filterYearTo}
                          onChange={e => setFilterYearTo(Math.max(Number(e.target.value), filterYearFrom))}
                          className="flex-1" />
                      </div>
                    </div>

                    {/* Price */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        {LOCALES[lang as 'ar' | 'en']?.maxPrice}: {filterPriceMax.toLocaleString()} {currencyLabel}
                      </label>
                      <input type="range" min={10000} max={1000000} step={5000} value={filterPriceMax}
                        onChange={e => setFilterPriceMax(Number(e.target.value))}
                        className="w-full" />
                    </div>

                    <button
                      onClick={submitSearch}
                      className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-sm transition-colors shadow-lg shadow-blue-200"
                    >
                      {LOCALES[lang as 'ar' | 'en']?.applyFilters}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3 pt-1">
                <Button asChild onClick={addRipple} className="ripple-wrapper h-11 rounded-xl bg-blue-600 hover:bg-blue-700 px-6 font-black text-white shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5">
                  <Link href="/buy">{LOCALES[lang as 'ar' | 'en']?.startBuying} <ArrowUpRight className="h-4 w-4 ltr:ml-1 rtl:mr-1 rtl:-rotate-90" /></Link>
                </Button>
                <Button asChild onClick={addRipple} className="ripple-wrapper h-11 rounded-xl border border-orange-400/50 bg-white/10 px-6 font-black text-white backdrop-blur hover:bg-orange-500 hover:border-orange-500 transition-all hover:-translate-y-0.5">
                  <Link href="/sell">{LOCALES[lang as 'ar' | 'en']?.sellMyCar} <CircleDollarSign className="h-4 w-4 ltr:ml-1 rtl:mr-1" /></Link>
                </Button>
              </div>
            </div>

            {/* Hero right — Futuristic Control Center & Active Metrics */}
            <div className="flex flex-col justify-center relative h-auto md:h-[450px] mt-8 md:mt-0">
              {/* Background glow effects */}
              <div className="absolute top-10 right-10 w-72 h-72 rounded-full bg-blue-500/10 blur-[80px]" />
              <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-orange-500/10 blur-[80px]" />
              
              {/* Central Card: Glassmorphic Car Display */}
              <div className="relative rounded-[2.5rem] border border-white/10 bg-white/5 backdrop-blur-xl p-5 shadow-2xl shadow-blue-950/40 w-full max-w-[420px] mx-auto transition-transform hover:scale-[1.01] duration-300">
                <div className="relative h-40 md:h-48 rounded-[2rem] overflow-hidden bg-slate-900 border border-white/5">
                  <img 
                    src="https://images.unsplash.com/photo-1617788138017-80ad40651399?w=600&q=80" 
                    alt="Premium Electric Vehicle" 
                    className="w-full h-full object-cover opacity-80"
                  />
                  {/* Floating badge */}
                  <div className="absolute top-4 left-4 bg-orange-500 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-lg">
                    {LOCALES[lang as 'ar' | 'en']?.liveAuction}
                  </div>
                  
                  {/* Glass bottom bar */}
                  <div className="absolute inset-x-0 bottom-0 bg-slate-950/40 backdrop-blur-md p-3 border-t border-white/5 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-slate-300 font-bold uppercase tracking-wider">{LOCALES[lang as 'ar' | 'en']?.currentBid}</p>
                      <p className="text-sm font-black text-white">14,250 {currencyLabel}</p>
                    </div>
                    <span className="flex h-2.5 w-2.5 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                    </span>
                  </div>
                </div>

                {/* Sub-metrics overlay grid */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {/* Item 1: AI Diagnosis */}
                  <div className="rounded-2xl border border-white/8 bg-white/5 p-3 flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/20">
                      <ShieldCheck className="h-4 w-4" />
                    </span>
                    <div>
                      <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">{LOCALES[lang as 'ar' | 'en']?.aiReport}</span>
                      <span className="block text-[11px] font-black text-emerald-300">{LOCALES[lang as 'ar' | 'en']?.verified}</span>
                    </div>
                  </div>

                  {/* Item 2: Active Bidders */}
                  <div className="rounded-2xl border border-white/8 bg-white/5 p-3 flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20">
                      <Users className="h-4 w-4" />
                    </span>
                    <div>
                      <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">{LOCALES[lang as 'ar' | 'en']?.activity}</span>
                      <span className="block text-[11px] font-black text-blue-300">{LOCALES[lang as 'ar' | 'en']?.bidders}</span>
                    </div>
                  </div>

                  {/* Item 3: Spare parts count */}
                  <div className="rounded-2xl border border-white/8 bg-white/5 p-3 flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-500/10 text-purple-400 ring-1 ring-purple-500/20">
                      <Boxes className="h-4 w-4" />
                    </span>
                    <div>
                      <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">{LOCALES[lang as 'ar' | 'en']?.inventory}</span>
                      <span className="block text-[11px] font-black text-purple-300">{LOCALES[lang as 'ar' | 'en']?.partsCount}</span>
                    </div>
                  </div>

                  {/* Item 4: Top Rating */}
                  <div className="rounded-2xl border border-white/8 bg-white/5 p-3 flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-orange-500/10 text-orange-400 ring-1 ring-orange-500/20">
                      <Star className="h-4 w-4 fill-orange-400 text-orange-400" />
                    </span>
                    <div>
                      <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">{LOCALES[lang as 'ar' | 'en']?.workshops}</span>
                      <span className="block text-[11px] font-black text-orange-300">{LOCALES[lang as 'ar' | 'en']?.workshopRating}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extra floating badge */}
              <div className="absolute top-12 left-8 border border-white/10 bg-slate-900/60 backdrop-blur-xl rounded-2xl p-2.5 flex items-center gap-2 shadow-xl animate-bounce" style={{ animationDuration: '6s' }}>
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <span className="text-[10px] font-black text-slate-200">{LOCALES[lang as 'ar' | 'en']?.systemOnline}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          AI PERSONALIZED RECOMMENDATIONS
      ══════════════════════════════════════════════════════ */}
      <section className="max-w-[1440px] mx-auto w-full px-4 pt-8 pb-4 section-fade">
        <AIPersonalizedRecommendations />
      </section>

      {/* ══════════════════════════════════════════════════════
          MAIN SERVICES GRID
      ══════════════════════════════════════════════════════ */}
      <section className="max-w-[1440px] mx-auto w-full px-4 py-6 section-fade">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-5">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-500 mb-1">{LOCALES[lang as 'ar' | 'en']?.mainServicesSub}</p>
            <h2 className="text-2xl font-black leading-tight text-slate-900 dark:text-white">{LOCALES[lang as 'ar' | 'en']?.mainServicesTitle}</h2>
          </div>
          <div className="hidden md:flex items-center gap-4 text-xs font-bold text-slate-500 shrink-0">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-600" />{LOCALES[lang as 'ar' | 'en']?.blueHint}</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-orange-500" />{LOCALES[lang as 'ar' | 'en']?.orangeHint}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 auto-rows-fr">
          {translatedMainServices.map((svc) => (
            <Link key={svc.id} href={svc.href} onClick={addRipple} className="h-full">
              <div className={`ripple-wrapper group relative h-full min-h-[178px] rounded-2xl bg-white dark:bg-slate-900 border-2 ${svc.highlight ? 'border-orange-200 dark:border-orange-900/50' : 'border-slate-100 dark:border-slate-800'} overflow-hidden card-hover cursor-pointer shadow-sm hover:shadow-xl`}>
                {/* Top color bar */}
                <div className={`h-1 w-full bg-gradient-to-r ${svc.accent}`} />
                {/* Background gradient blob */}
                <div className={`absolute -bottom-8 -right-8 h-28 w-28 rounded-full bg-gradient-to-br ${svc.accent} opacity-10 group-hover:opacity-20 transition-opacity`} />
                <svc.icon className="absolute bottom-3 right-3 h-14 w-14 text-slate-900/[0.04] dark:text-white/[0.04] group-hover:scale-110 transition-transform duration-300" />

                <div className="relative p-4 flex flex-col h-full justify-between gap-3">
                  <div className="min-w-0">
                    <div className={`h-11 w-11 rounded-xl ${svc.iconBg} flex items-center justify-center mb-3 shadow-sm ring-1 ring-slate-100 group-hover:scale-105 transition-transform`}>
                      <svc.icon className="h-5 w-5" />
                    </div>
                    <span className="block min-h-[34px] text-sm font-black text-slate-900 dark:text-white leading-tight line-clamp-2">{svc.label}</span>
                    <span className="block min-h-[12px] text-[9px] font-black uppercase tracking-widest text-slate-400 mt-0.5 truncate">{svc.sub}</span>
                    <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 leading-relaxed mt-2 line-clamp-2">{svc.desc}</p>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100/80 dark:border-slate-800">
                    <span className="text-[11px] font-black text-blue-600 dark:text-blue-400">{LOCALES[lang as 'ar' | 'en']?.open}</span>
                    <ArrowUpRight className="h-4 w-4 text-orange-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 rtl:-rotate-90 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          RECOMMENDED CARS
      ══════════════════════════════════════════════════════ */}
      <section className="max-w-[1440px] mx-auto w-full px-4 py-6 section-fade">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 mb-1">{LOCALES[lang as 'ar' | 'en']?.recSub}</p>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-500" /> {LOCALES[lang as 'ar' | 'en']?.recTitle}
            </h2>
          </div>
          <Link href="/buy" className="flex items-center gap-1.5 text-sm font-black text-blue-600 hover:text-blue-700 transition-colors group">
            {LOCALES[lang as 'ar' | 'en']?.viewAll} <ChevronRight className="h-4 w-4 rtl:rotate-180 ltr:group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 transition-transform" />
          </Link>
        </div>
        <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-3">
          {recommendedCars.map((car) => (
            <Link key={car.id} href={`/buy/listing/${car.id}`} className="shrink-0 w-64 group">
              <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm hover:shadow-xl card-hover cursor-pointer">
                <div className="relative h-40 bg-slate-100 overflow-hidden">
                  <img src={car.image_url} alt={car.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 right-3">
                    <span className="bg-blue-600 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-md">
                      {car.year}
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-900/60 to-transparent" />
                </div>
                <div className="p-3.5">
                  <h3 className="font-black text-slate-900 dark:text-white text-sm truncate">{car.title}</h3>
                  <div className="flex items-center gap-1.5 mt-1 text-slate-400 text-[10px] font-bold">
                    <MapPin className="h-3 w-3" /> {LOCALES[lang as 'ar' | 'en']?.available}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-base font-black text-blue-600 dark:text-blue-400">
                      {car.price?.toLocaleString()} <span className="text-xs">{currencyLabel}</span>
                    </span>
                    <span className="flex items-center gap-1 text-orange-500 text-[10px] font-black">
                      <Zap className="h-3 w-3" /> {LOCALES[lang as 'ar' | 'en']?.featured}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          AUCTION PRICE CALCULATOR
      ══════════════════════════════════════════════════════ */}
      <section className="max-w-[1440px] mx-auto w-full px-4 py-6 section-fade">
        <div className="rounded-3xl bg-gradient-to-br from-[#0f2044] to-[#1a3a6e] p-6 md:p-8 shadow-2xl text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/10 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-2xl bg-orange-500/20 border border-orange-400/30 flex items-center justify-center">
                <Calculator className="h-6 w-6 text-orange-300" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-300">{LOCALES[lang as 'ar' | 'en']?.calcSub}</p>
                <h2 className="text-xl font-black text-white">{LOCALES[lang as 'ar' | 'en']?.calcTitle}</h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.calcMake}</label>
                  <select value={calcMake} onChange={e => { setCalcMake(e.target.value); setCalcResult(null); }}
                    className="w-full h-10 rounded-xl bg-white/10 border border-white/15 text-white text-sm font-bold px-3 outline-none focus:border-orange-400 backdrop-blur">
                    {CAR_MAKES.map(m => <option key={m} value={m} className="text-slate-800 bg-white">{m}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.calcYear}</label>
                  <select value={calcYear} onChange={e => { setCalcYear(Number(e.target.value)); setCalcResult(null); }}
                    className="w-full h-10 rounded-xl bg-white/10 border border-white/15 text-white text-sm font-bold px-3 outline-none focus:border-orange-400 backdrop-blur">
                    {YEARS.map(y => <option key={y} value={y} className="text-slate-800 bg-white">{y}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.calcKm}</label>
                  <input type="number" min={0} max={500} value={calcKm}
                    onChange={e => { setCalcKm(Number(e.target.value)); setCalcResult(null); }}
                    className="w-full h-10 rounded-xl bg-white/10 border border-white/15 text-white text-sm font-bold px-3 outline-none focus:border-orange-400 backdrop-blur placeholder:text-white/40"
                    placeholder="e.g. 50" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{LOCALES[lang as 'ar' | 'en']?.calcCondition}</label>
                  <select value={calcCondition} onChange={e => { setCalcCondition(e.target.value); setCalcResult(null); }}
                    className="w-full h-10 rounded-xl bg-white/10 border border-white/15 text-white text-sm font-bold px-3 outline-none focus:border-orange-400 backdrop-blur">
                    {CONDITIONS.map(c => <option key={c} value={c} className="text-slate-800 bg-white">{CONDITIONS_TRANS[c]?.[lang as 'ar' | 'en'] || c}</option>)}
                  </select>
                </div>
                <button
                  onClick={() => setCalcResult(calcAuctionPrice(calcMake, calcYear, calcKm, calcCondition))}
                  className="col-span-2 h-11 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-black text-sm transition-colors shadow-lg shadow-orange-500/30 btn-pulse flex items-center justify-center gap-2"
                >
                  <Calculator className="h-4 w-4" /> {LOCALES[lang as 'ar' | 'en']?.calcBtn}
                </button>
              </div>

              <div className="flex flex-col items-center justify-center h-full min-h-[160px] rounded-2xl bg-white/5 border border-white/10 p-6 text-center">
                {calcResult ? (
                  <div className="space-y-2">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">{LOCALES[lang as 'ar' | 'en']?.calcEst}</p>
                    <p className="text-4xl font-black text-white">
                      {calcResult.toLocaleString()}
                    </p>
                    <p className="text-sm font-bold text-orange-300">{currencyLabel}</p>
                    <p className="text-[10px] text-slate-400 font-medium">
                      {lang === 'ar' 
                        ? `بناءً على بيانات السوق لسيارات ${calcMake} موديل ${calcYear} · ممشى ${calcKm} ألف كم · حالة ${CONDITIONS_TRANS[calcCondition]?.ar || calcCondition}`
                        : `Based on market data for ${calcMake} ${calcYear} · ${calcKm}k km · ${calcCondition}`
                      }
                    </p>
                    <Link href="/auctions">
                      <button className="mt-3 h-9 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-black transition-colors">
                        {LOCALES[lang as 'ar' | 'en']?.calcBrowse}
                      </button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2 text-slate-400">
                    <Calculator className="h-12 w-12 mx-auto opacity-30" />
                    <p className="text-sm font-bold">{LOCALES[lang as 'ar' | 'en']?.calcPlaceholderTitle}</p>
                    <p className="text-xs opacity-70">{LOCALES[lang as 'ar' | 'en']?.calcPlaceholderSub}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════
          TOP WORKSHOPS
      ══════════════════════════════════════════════════════ */}
      <section className="max-w-[1440px] mx-auto w-full px-4 py-6 section-fade">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-500 mb-1">{LOCALES[lang as 'ar' | 'en']?.wsSub}</p>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2">
              <Wrench className="h-5 w-5 text-blue-600" /> {LOCALES[lang as 'ar' | 'en']?.wsTitle}
            </h2>
          </div>
          <Link href="/maintenance" className="flex items-center gap-1.5 text-sm font-black text-blue-600 hover:text-blue-700 transition-colors group">
            {LOCALES[lang as 'ar' | 'en']?.viewAll} <ChevronRight className="h-4 w-4 rtl:rotate-180 ltr:group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 transition-transform" />
          </Link>
        </div>

        {workshops.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workshops.map((ws: any) => (
              <Link key={ws.id} href="/maintenance">
                <div className="group rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm hover:shadow-xl card-hover cursor-pointer">
                  <div className="relative h-32 bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden">
                    {ws.image ? (
                      <img src={ws.image} alt="" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Wrench className="h-12 w-12 text-white/20" />
                      </div>
                    )}
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-2.5 py-1">
                      <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                      <span className="text-[11px] font-black text-slate-800">{ws.rating || '4.8'}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-black text-slate-900 dark:text-white text-sm truncate">{ws.name_ar || ws.name || 'Workshop'}</h3>
                    <div className="flex items-center gap-1.5 mt-1 text-slate-400 text-[10px] font-bold">
                      <MapPin className="h-3 w-3" /> {ws.location || LOCALES[lang as 'ar' | 'en']?.available}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] font-black uppercase text-blue-600 bg-blue-50 dark:bg-blue-950/30 px-2.5 py-1 rounded-full">{ws.category || 'General'}</span>
                      <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
                        <Clock className="h-3 w-3" /> {LOCALES[lang as 'ar' | 'en']?.wsAvailable}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* Skeleton / placeholder when workshops not loaded from API */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: lang === 'ar' ? 'ورشة الفارس لصيانة السيارات' : 'Al Faris Auto Workshop', location: lang === 'ar' ? 'مسقط' : 'Muscat', category: lang === 'ar' ? 'المحركات' : 'Engine', rating: '4.9' },
              { name: lang === 'ar' ? 'مركز خدمات نجمة السيارات' : 'Star Motors Service', location: lang === 'ar' ? 'صحار' : 'Sohar', category: lang === 'ar' ? 'الكهرباء' : 'Electrical', rating: '4.8' },
              { name: lang === 'ar' ? 'مركز النخبة للعناية بالسيارات' : 'Elite Car Care Center', location: lang === 'ar' ? 'صلالة' : 'Salalah', category: lang === 'ar' ? 'الهيكل والطلاء' : 'Body & Paint', rating: '4.7' },
              { name: lang === 'ar' ? 'كراج الخليج للسيارات' : 'Gulf Auto Garage', location: lang === 'ar' ? 'أبو ظبي' : 'Abu Dhabi', category: lang === 'ar' ? 'المكابح' : 'Brakes', rating: '4.9' },
              { name: lang === 'ar' ? 'ورشة العمل الممتازة' : 'Premium Workshop', location: lang === 'ar' ? 'دبي' : 'Dubai', category: lang === 'ar' ? 'تكييف الهواء' : 'A/C Service', rating: '4.6' },
              { name: lang === 'ar' ? 'ماستر تك لصيانة السيارات' : 'Master Tech Auto', location: lang === 'ar' ? 'مدينة الكويت' : 'Kuwait City', category: lang === 'ar' ? 'عام' : 'General', rating: '4.8' },
            ].map((ws, i) => (
              <Link key={i} href="/maintenance">
                <div className="group rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm hover:shadow-xl card-hover cursor-pointer">
                  <div className={`relative h-32 overflow-hidden bg-gradient-to-br ${['from-blue-700 to-blue-900','from-orange-700 to-amber-900','from-emerald-700 to-teal-900','from-indigo-700 to-purple-900','from-rose-700 to-pink-900','from-sky-700 to-cyan-900'][i % 6]}`}>
                    <Wrench className="absolute bottom-3 right-3 h-12 w-12 text-white/10" />
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-2.5 py-1">
                      <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                      <span className="text-[11px] font-black text-slate-800">{ws.rating}</span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-3">
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, s) => <Star key={s} className="h-2.5 w-2.5 text-yellow-400 fill-yellow-400" />)}
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-black text-slate-900 dark:text-white text-sm truncate">{ws.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1 text-slate-400 text-[10px] font-bold">
                      <MapPin className="h-3 w-3" /> {ws.location}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] font-black uppercase text-blue-600 bg-blue-50 dark:bg-blue-950/30 px-2.5 py-1 rounded-full">{ws.category}</span>
                      <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
                        <Clock className="h-3 w-3" /> {LOCALES[lang as 'ar' | 'en']?.available}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════════════════
          FOOTER CTA BANNER
      ══════════════════════════════════════════════════════ */}
      <section className="max-w-[1440px] mx-auto w-full px-4 py-6 pb-20 md:pb-8 section-fade">
        <div className="rounded-3xl bg-gradient-to-r from-orange-500 to-amber-500 p-7 text-white text-center relative overflow-hidden shadow-xl shadow-orange-200">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
          <div className="relative">
            <h3 className="text-2xl font-black mb-2">{LOCALES[lang as 'ar' | 'en']?.ctaTitle}</h3>
            <p className="text-white/80 text-sm font-medium mb-5">{LOCALES[lang as 'ar' | 'en']?.ctaSub}</p>
            <Button asChild onClick={addRipple} className="ripple-wrapper h-12 rounded-xl bg-white text-orange-600 hover:bg-orange-50 font-black px-8 shadow-lg hover:-translate-y-0.5 transition-all">
              <Link href="/sell">{LOCALES[lang as 'ar' | 'en']?.ctaBtn} <ArrowUpRight className="h-4 w-4 ltr:ml-1 rtl:mr-1 rtl:-rotate-90" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
